import { useId, useMemo, useState } from "react";
import { formatInt } from "@/lib/format";

export interface Series {
  key: string;
  label: string;
  color: string;
}

/**
 * Columns over an ordered x axis (e.g. hours after a send), optionally stacked. Thin columns
 * (<= 24px) with a 4px rounded cap, a 2px surface gap between stacked parts, hairline grid,
 * one y axis, hover tooltip per column, legend for 2+ series, and a table view for screen readers.
 */
export function ColumnChart<T extends Record<string, number | string>>({
  data,
  series,
  xKey,
  xLabel,
  tooltipTitle,
  height = 180,
  title,
}: {
  data: T[];
  series: Series[];
  xKey: keyof T;
  xLabel: (row: T) => string;
  tooltipTitle: (row: T) => string;
  height?: number;
  title: string;
}) {
  const id = useId();
  const [hover, setHover] = useState<number | null>(null);
  const [showTable, setShowTable] = useState(false);
  const width = 720;
  const pad = { top: 8, right: 8, bottom: 22, left: 40 };
  const innerW = width - pad.left - pad.right;
  const innerH = height - pad.top - pad.bottom;
  const totals = data.map((d) => series.reduce((a, s) => a + Number(d[s.key] ?? 0), 0));
  const max = Math.max(1, ...totals);
  const ticks = useMemo(() => niceTicks(max), [max]);
  const top = ticks[ticks.length - 1];
  const slot = innerW / Math.max(data.length, 1);
  const colW = Math.max(2, Math.min(24, slot - 2));
  const y = (v: number) => pad.top + innerH - (v / top) * innerH;
  const labelEvery = Math.ceil(data.length / 12);
  const h = hover !== null ? data[hover] : null;
  return (
    <figure className="relative">
      <figcaption className="mb-1 flex items-center gap-3 text-xs text-[var(--color-muted-foreground)]">
        <span className="font-medium text-[var(--color-foreground)]">{title}</span>
        {series.length > 1 &&
          series.map((s) => (
            <span key={s.key} className="inline-flex items-center gap-1">
              <span aria-hidden className="inline-block size-2.5 rounded-[2px]" style={{ background: s.color }} />
              {s.label}
            </span>
          ))}
        <button className="ml-auto underline-offset-2 hover:underline" onClick={() => setShowTable((v) => !v)} aria-expanded={showTable} aria-controls={`${id}-table`}>
          {showTable ? "Show chart" : "Show as table"}
        </button>
      </figcaption>
      {!showTable && (
        <svg viewBox={`0 0 ${width} ${height}`} className="w-full" role="img" aria-label={`${title}. Use "Show as table" for the numbers.`} onMouseLeave={() => setHover(null)}>
          {ticks.map((t) => (
            <g key={t}>
              <line x1={pad.left} x2={width - pad.right} y1={y(t)} y2={y(t)} stroke="var(--color-chart-grid)" strokeWidth={1} />
              <text x={pad.left - 6} y={y(t)} dy="0.32em" textAnchor="end" className="num" fontSize={10} fill="var(--color-muted-foreground)">
                {compact(t)}
              </text>
            </g>
          ))}
          {data.map((d, i) => {
            const cx = pad.left + slot * i + slot / 2;
            let acc = 0;
            const parts = series
              .map((s) => {
                const v = Number(d[s.key] ?? 0);
                const y0 = y(acc);
                acc += v;
                const y1 = y(acc);
                return { s, v, y0, y1 };
              })
              .filter((p) => p.v > 0);
            return (
              <g key={String(d[xKey])}>
                {parts.map((p, j) => {
                  const isTop = j === parts.length - 1;
                  const gap = j > 0 ? 2 : 0; // surface gap between stacked parts
                  const hgt = Math.max(0, p.y0 - p.y1 - gap);
                  return (
                    <path
                      key={p.s.key}
                      d={colPath(cx - colW / 2, p.y1, colW, hgt, isTop ? Math.min(4, hgt, colW / 2) : 0)}
                      fill={p.s.color}
                      opacity={hover === null || hover === i ? 1 : 0.55}
                    />
                  );
                })}
                {i % labelEvery === 0 && (
                  <text x={cx} y={height - 6} textAnchor="middle" fontSize={10} className="num" fill="var(--color-muted-foreground)">
                    {xLabel(d)}
                  </text>
                )}
                <rect x={pad.left + slot * i} y={pad.top} width={slot} height={innerH} fill="transparent" onMouseEnter={() => setHover(i)} onFocus={() => setHover(i)} />
              </g>
            );
          })}
          <line x1={pad.left} x2={width - pad.right} y1={y(0)} y2={y(0)} stroke="var(--color-border)" strokeWidth={1} />
        </svg>
      )}
      {!showTable && h && hover !== null && (
        <div
          className="pointer-events-none absolute z-10 min-w-40 rounded-md border bg-[var(--color-card)] px-2.5 py-1.5 text-xs shadow-lg"
          style={{ left: `${Math.min(80, ((pad.left + slot * hover + slot) / width) * 100)}%`, top: 24 }}
        >
          <div className="mb-1 font-medium">{tooltipTitle(h)}</div>
          {series.map((s) => (
            <div key={s.key} className="flex items-center gap-2">
              <span aria-hidden className="inline-block size-2 rounded-[2px]" style={{ background: s.color }} />
              <span className="flex-1 text-[var(--color-muted-foreground)]">{s.label}</span>
              <span className="num">{formatInt(Number(h[s.key] ?? 0))}</span>
            </div>
          ))}
        </div>
      )}
      {showTable && (
        <div id={`${id}-table`} className="max-h-72 overflow-y-auto rounded border">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-[var(--color-muted)] text-left">
              <tr>
                <th className="px-2 py-1 font-medium">When</th>
                {series.map((s) => (
                  <th key={s.key} className="px-2 py-1 text-right font-medium">
                    {s.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((d) => (
                <tr key={String(d[xKey])} className="border-t">
                  <td className="px-2 py-0.5">{tooltipTitle(d)}</td>
                  {series.map((s) => (
                    <td key={s.key} className="num px-2 py-0.5 text-right">
                      {formatInt(Number(d[s.key] ?? 0))}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </figure>
  );
}

/** A column with only its top corners rounded (square at the baseline). */
function colPath(x: number, y: number, w: number, h: number, r: number): string {
  if (h <= 0) return "";
  return `M${x},${y + h}V${y + r}Q${x},${y} ${x + r},${y}H${x + w - r}Q${x + w},${y} ${x + w},${y + r}V${y + h}Z`;
}

/** Clean y-axis ticks for counts: 0 and 3-5 whole-number steps covering max. */
export function niceTicks(max: number): number[] {
  const rough = max / 4;
  const pow = 10 ** Math.floor(Math.log10(rough));
  const step = Math.max(1, [1, 2, 2.5, 5, 10].map((m) => m * pow).find((s) => s >= rough) ?? 10 * pow);
  const out: number[] = [];
  for (let v = 0; v <= max + step * 0.0001 || out.length < 2; v += step) out.push(Math.round(v * 1000) / 1000);
  if (out[out.length - 1] < max) out.push(out[out.length - 1] + step);
  return out;
}

function compact(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}K`;
  return String(n);
}
